import { useEffect, useState } from 'react'
import { OrbitControls } from '@react-three/drei'
import ThreeEnvironmentSplat from './ThreeEnvironmentSplat'
import LightProbeVolume from './LightProbeVolume'
import MaterialBallProbe from './MaterialBallProbe'
import { listEnvironments } from '../lib/environmentsApi'

/**
 * Scene contents for ?debug_env=1 — NO gaussian characters. Loads an
 * environment splat, queries the ENG-561 baked lighting volumes (diffuse SH
 * grid + specular reflection captures, baked by the lighting service), and
 * shades 3js mesh balls with the BecomingLit equation so the env integration
 * and the shading itself can be eyeballed.
 *
 * Env resolution (all overridable by URL):
 *   ?splat=<url>          direct splat URL (skips the AAM lookup)
 *   ?character=<slug>     AAM character to read environments from (def isaac-fused)
 *   ?env=<name>           environment name to pick (def MessyComputerLabOffice)
 *   (there is no ?renderer any more: three draws it, because three is the only
 *   renderer there is)
 *   ?pose=1               apply the environment row's own default_pose
 *
 * ?pose is OPT-IN because this harness exists to judge BAKED LIGHTING, and the
 * probe grid is baked in the splat's own space — posing the mesh would move the
 * geometry out from under it. For LOOKING at an environment
 * you want it, or the default camera can end up inside an unposed room. It needs
 * the AAM row, so it does nothing alongside a direct ?splat= URL.
 */
const RIG_POS = [0, 1.4, 0]

async function resolveEnvSplat() {
  const p = new URLSearchParams(window.location.search)
  const direct = p.get('splat')
  // A direct URL carries no row, so there is no pose to apply — say so rather
  // than silently rendering unposed when ?pose=1 was asked for.
  if (direct) return { url: direct, pose: null }
  const character = p.get('character') || 'isaac-fused'
  const want = (p.get('env') || 'MessyComputerLabOffice').toLowerCase()
  // listEnvironments → assetManagerApi (VITE_ASSET_MANAGER_URL=/aam proxy with
  // X-API-Key). No silent fallback — a failure throws and surfaces in the HUD.
  const list = await listEnvironments(character)
  const match = list.find(e => (e.name || '').toLowerCase() === want) || list[0]
  const fileUrl = match?.fileUrl ?? match?.file_url // AAM JSON is camelCase
  if (!fileUrl) throw new Error(`no environment "${want}" for ${character}`)
  return { url: fileUrl, pose: match?.defaultPose ?? match?.default_pose ?? null }
}

export default function DebugEnvScene({ onStatus }) {
  const [splatUrl, setSplatUrl] = useState(null)
  const [pose, setPose] = useState(null)
  const params = new URLSearchParams(window.location.search)
  const wantPose = params.get('pose') === '1'

  useEffect(() => {
    let cancelled = false
    onStatus?.({ env: 'resolving' })
    resolveEnvSplat()
      .then(({ url, pose: p }) => {
        if (cancelled) return
        setSplatUrl(url); setPose(wantPose ? p : null)
        onStatus?.({ env: 'loaded', splatUrl: url,
          pose: !wantPose ? 'not applied (?pose=1 to apply)' : p ? 'applied' : 'none on this row' })
      })
      .catch(err => { if (!cancelled) { console.error('[env-debug]', err); onStatus?.({ env: 'error', error: String(err.message ?? err) }) } })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {splatUrl && (
        <>
          {/* poseScale of the bake is 1 → pose the splat at scale 1, UNLESS the
             row's own pose was asked for (?pose=1); see the header note. */}
          <ThreeEnvironmentSplat
            key={splatUrl}
            url={splatUrl}
            position={pose ? [pose.px ?? 0, pose.py ?? 0, pose.pz ?? 0] : [0, 0, 0]}
            rotation={pose ? [pose.rx ?? 0, pose.ry ?? 0, pose.rz ?? 0] : [0, 0, 0]}
            scale={pose?.scale ?? 1}
          />
          {/* The SH light-probe grid is the ONLY env source — both diffuse and
             specular sample this volume (no reflection-capture images). */}
          <LightProbeVolume splatUrl={splatUrl} />
          {/* The balls are the LIGHTING harness. They draw in the SAME scene as
             the environment now — there is no overlay canvas to be hidden
             underneath, which is what used to make them present but invisible. */}
          <MaterialBallProbe position={RIG_POS} />
        </>
      )}
      <OrbitControls target={RIG_POS} enablePan enableZoom />
    </>
  )
}
